import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useModal } from '../contexts/ModalContext';
import { db, rtdb } from '../firebase/config';
import { collection, query, limit, getDocs, doc, getDoc } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { Users, UserPlus, UserCheck, MessageCircle, Search, Clock, Check, X, ShieldCheck } from 'lucide-react';

const FriendCard = ({
    user,
    status, // 'friend' | 'requested' | 'incoming' | 'none'
    currentUserId,
    onSendRequest,
    onCancelRequest,
    onAcceptRequest,
    onDeclineRequest,
    onRemoveFriend,
    navigate
}) => {
    const [isOnline, setIsOnline] = useState(false);

    useEffect(() => {
        if (!rtdb || !user.id) return;
        const statusRef = ref(rtdb, `/status/${user.id}`);
        const unsub = onValue(statusRef, (snap) => {
            setIsOnline(snap.val()?.state === 'online');
        });
        return () => unsub();
    }, [user.id]);

    const handleChat = (e) => {
        e.stopPropagation();
        const chatId = [currentUserId, user.id].sort().join('_');
        navigate('chat', { chatId });
    };

    return (
        <div
            onClick={() => navigate('profile', { userId: user.id })}
            className="p-3.5 bg-white dark:bg-[#111827] border border-slate-200/90 dark:border-slate-800/90 rounded-2xl flex items-center justify-between gap-3 cursor-pointer group hover:border-slate-300 dark:hover:border-slate-700 transition-all shadow-xs"
        >
            <div className="flex items-center gap-3 min-w-0">
                <div className="relative flex-shrink-0">
                    <div className="w-11 h-11 rounded-full p-[1.5px] bg-gradient-to-tr from-brand-500 to-indigo-500">
                        <img
                            src={user.photoURL || `https://api.dicebear.com/7.x/bottts/svg?seed=${user.id}`}
                            alt=""
                            className="w-full h-full rounded-full object-cover bg-white dark:bg-slate-900"
                        />
                    </div>
                    {isOnline && (
                        <span className="absolute bottom-0 right-0 w-3 h-3 bg-emerald-500 border-2 border-white dark:border-[#111827] rounded-full"></span>
                    )}
                </div>

                <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                        <h4 className="font-semibold text-sm text-slate-900 dark:text-slate-100 group-hover:text-brand-500 transition-colors truncate">
                            {user.displayName}
                        </h4>
                        {isOnline && <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium">● Online</span>}
                    </div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">@{user.username || 'user'}</p>
                    {user.bio && <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">{user.bio}</p>}
                </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                {status === 'friend' && (
                    <button
                        onClick={handleChat}
                        title="Send Message"
                        className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700/80 text-slate-700 dark:text-slate-300 transition-colors"
                    >
                        <MessageCircle className="w-4 h-4" />
                    </button>
                )}

                {status === 'friend' && (
                    <button
                        onClick={() => onRemoveFriend(user)}
                        title="Click to remove friend"
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-slate-100 hover:bg-rose-50 hover:text-rose-600 dark:bg-slate-800 dark:hover:bg-rose-950/30 dark:hover:text-rose-400 text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-700 transition-all"
                    >
                        <UserCheck className="w-3.5 h-3.5 text-brand-500" />
                        <span>Friends</span>
                    </button>
                )}

                {status === 'requested' && (
                    <button
                        onClick={() => onCancelRequest(user.id)}
                        title="Click to cancel request"
                        className="px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-amber-50 dark:bg-amber-500/15 text-amber-600 dark:text-amber-400 border border-amber-500/30 hover:bg-rose-50 hover:text-rose-600 transition-all"
                    >
                        <Clock className="w-3.5 h-3.5" />
                        <span>Requested</span>
                    </button>
                )}

                {status === 'incoming' && (
                    <div className="flex items-center gap-1.5">
                        <button
                            onClick={() => onAcceptRequest(user.id)}
                            title="Accept friend request"
                            className="px-3 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1 bg-brand-500 hover:bg-brand-600 text-white shadow-xs transition-all"
                        >
                            <Check className="w-3.5 h-3.5" />
                            <span>Accept</span>
                        </button>
                        <button
                            onClick={() => onDeclineRequest(user.id)}
                            title="Decline request"
                            className="p-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-rose-500 transition-colors"
                        >
                            <X className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}

                {status === 'none' && (
                    <button
                        onClick={() => onSendRequest(user.id)}
                        className="px-3.5 py-1.5 rounded-xl text-xs font-semibold flex items-center gap-1.5 bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm active:scale-[0.98] transition-all"
                    >
                        <UserPlus className="w-3.5 h-3.5" />
                        <span>Add Friend</span>
                    </button>
                )}
            </div>
        </div>
    );
};

const FriendsPage = ({ navigate }) => {
    const {
        currentUser,
        userProfile,
        sendFriendRequest,
        cancelFriendRequest,
        acceptFriendRequest,
        declineFriendRequest,
        removeFriend
    } = useAuth();
    const { openModal } = useModal();
    const [activeTab, setActiveTab] = useState('friends'); // 'friends' | 'requests' | 'discover'
    const [friendsData, setFriendsData] = useState([]);
    const [requestsData, setRequestsData] = useState([]);
    const [discoverUsers, setDiscoverUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);

    const friendIds = userProfile?.friends || [];
    const incomingIds = userProfile?.incomingFriendRequests || [];
    const outgoingIds = userProfile?.outgoingFriendRequests || [];

    // Load user's friends & incoming requests data on demand
    useEffect(() => {
        if (!currentUser) return;

        const loadNetwork = async () => {
            setLoading(true);
            try {
                // 1. Fetch friend profiles
                if (friendIds.length > 0) {
                    const friendsPromises = friendIds.slice(0, 50).map(async (fId) => {
                        const snap = await getDoc(doc(db, 'users', fId));
                        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
                    });
                    const results = (await Promise.all(friendsPromises)).filter(Boolean);
                    setFriendsData(results);
                } else {
                    setFriendsData([]);
                }

                // 2. Fetch incoming request profiles
                if (incomingIds.length > 0) {
                    const reqPromises = incomingIds.slice(0, 30).map(async (rId) => {
                        const snap = await getDoc(doc(db, 'users', rId));
                        return snap.exists() ? { id: snap.id, ...snap.data() } : null;
                    });
                    const results = (await Promise.all(reqPromises)).filter(Boolean);
                    setRequestsData(results);
                } else {
                    setRequestsData([]);
                }

                // 3. Scalable Discover: Load a capped suggestion set (limit 12)
                const discoverQuery = query(collection(db, 'users'), limit(15));
                const discoverSnap = await getDocs(discoverQuery);
                const suggested = [];
                discoverSnap.forEach(d => {
                    if (d.id !== currentUser.uid && !friendIds.includes(d.id) && !incomingIds.includes(d.id)) {
                        suggested.push({ id: d.id, ...d.data() });
                    }
                });
                setDiscoverUsers(suggested.slice(0, 12));

            } catch (err) {
                console.error("Error loading network:", err);
            } finally {
                setLoading(false);
            }
        };

        loadNetwork();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser, friendIds.length, incomingIds.length]);


    // Confirmation modal for removing friend
    const handleRemoveFriendWithConfirm = (user) => {
        openModal('confirm', {
            title: 'Remove Friend',
            message: `Are you sure you want to remove @${user.username || 'this user'} from your friends? You will need to send a new friend request to reconnect.`,
            confirmText: 'Remove',
            confirmColor: 'red',
            onConfirm: () => removeFriend(user.id)
        });
    };

    // Filter lists by search term
    const filterList = (list) => {
        if (!searchTerm.trim()) return list;
        const s = searchTerm.toLowerCase();
        return list.filter(u =>
            u.displayName?.toLowerCase().includes(s) ||
            u.username?.toLowerCase().includes(s)
        );
    };

    const displayedFriends = filterList(friendsData);
    const displayedRequests = filterList(requestsData);
    const displayedDiscover = filterList(discoverUsers);

    return (
        <div className="space-y-4 animate-fade-in pb-16">
            <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 tracking-tight">
                    Network
                </h2>
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                    {friendIds.length} Friends
                </span>
            </div>

            {/* 3-Way Segmented Tabs */}
            <div className="grid grid-cols-3 p-1 bg-slate-200/70 dark:bg-slate-900/90 rounded-2xl border border-slate-300/60 dark:border-slate-800/80 backdrop-blur-sm">
                <button
                    onClick={() => setActiveTab('friends')}
                    className={`py-2 rounded-xl text-xs font-semibold transition-all ${
                        activeTab === 'friends'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    Friends ({friendIds.length})
                </button>
                <button
                    onClick={() => setActiveTab('requests')}
                    className={`py-2 rounded-xl text-xs font-semibold transition-all relative ${
                        activeTab === 'requests'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    Requests
                    {incomingIds.length > 0 && (
                        <span className="ml-1.5 px-1.5 py-0.5 bg-sky-500 text-white rounded-full text-[10px] font-bold">
                            {incomingIds.length}
                        </span>
                    )}
                </button>
                <button
                    onClick={() => setActiveTab('discover')}
                    className={`py-2 rounded-xl text-xs font-semibold transition-all ${
                        activeTab === 'discover'
                            ? 'bg-white dark:bg-slate-800 text-slate-900 dark:text-white shadow-sm'
                            : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white'
                    }`}
                >
                    Discover
                </button>
            </div>


            {/* Search Input */}
            <div className="relative">
                <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder={`Search ${activeTab === 'friends' ? 'friends' : activeTab === 'requests' ? 'requests' : 'people'} by name or @username...`}
                    className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#111827] border border-slate-200 dark:border-slate-800 rounded-2xl text-slate-900 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 text-xs focus:outline-none focus:ring-2 focus:ring-brand-500/40 focus:border-brand-500 transition-all shadow-xs"
                />
            </div>

            {/* Scalable Notice for Discover */}
            {activeTab === 'discover' && (
                <div className="p-3 bg-sky-50/70 dark:bg-sky-500/10 border border-sky-200/80 dark:border-sky-500/20 rounded-2xl text-xs text-sky-800 dark:text-sky-300 flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-sky-500 flex-shrink-0" />
                    <span>Suggested active creators. Use search to look up any specific account.</span>
                </div>
            )}

            {/* Users List */}
            {loading ? (
                <div className="py-16 text-center space-y-3">
                    <div className="w-8 h-8 mx-auto rounded-full border-2 border-brand-500 border-t-transparent animate-spin" />
                    <p className="text-xs text-slate-400 dark:text-slate-500">Loading network...</p>
                </div>
            ) : activeTab === 'friends' ? (
                displayedFriends.length > 0 ? (
                    <div className="space-y-2">
                        {displayedFriends.map(u => (
                            <FriendCard
                                key={u.id}
                                user={u}
                                status="friend"
                                currentUserId={currentUser?.uid}
                                onRemoveFriend={handleRemoveFriendWithConfirm}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white dark:bg-[#111827] border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 space-y-3">
                        <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto text-slate-400 dark:text-slate-500">
                            <Users className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-200">
                            No friends yet
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                            Check the "Discover" tab to send friend requests to creators on Batcheet!
                        </p>
                    </div>
                )
            ) : activeTab === 'requests' ? (
                displayedRequests.length > 0 ? (
                    <div className="space-y-2">
                        {displayedRequests.map(u => (
                            <FriendCard
                                key={u.id}
                                user={u}
                                status="incoming"
                                currentUserId={currentUser?.uid}
                                onAcceptRequest={acceptFriendRequest}
                                onDeclineRequest={declineFriendRequest}
                                navigate={navigate}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white dark:bg-[#111827] border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 space-y-3">
                        <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto text-slate-400 dark:text-slate-500">
                            <Users className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-200">
                            No pending requests
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                            When someone sends you a friend request, it will appear here.
                        </p>
                    </div>
                )
            ) : (
                displayedDiscover.length > 0 ? (
                    <div className="space-y-2">
                        {displayedDiscover.map(u => {
                            const isOutgoing = outgoingIds.includes(u.id);
                            return (
                                <FriendCard
                                    key={u.id}
                                    user={u}
                                    status={isOutgoing ? 'requested' : 'none'}
                                    currentUserId={currentUser?.uid}
                                    onSendRequest={sendFriendRequest}
                                    onCancelRequest={cancelFriendRequest}
                                    navigate={navigate}
                                />
                            );
                        })}
                    </div>
                ) : (
                    <div className="text-center py-16 bg-white dark:bg-[#111827] border border-dashed border-slate-200 dark:border-slate-800 rounded-3xl p-6 space-y-3">
                        <div className="w-12 h-12 bg-slate-100 dark:bg-slate-800 rounded-2xl flex items-center justify-center mx-auto text-slate-400 dark:text-slate-500">
                            <Users className="w-6 h-6" />
                        </div>
                        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-200">
                            No creators found
                        </h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
                            Try searching with a different name or @username.
                        </p>
                    </div>
                )
            )}
        </div>
    );
};

export default FriendsPage;
